/**
 * @file SnackBarMessage.tsx
 *
 * @description This component displays custom temporary notifications for various system events such as successful form submissions,
 * errors, warnings, or informational messages.
 *
 * @module Modals
 * @requires Material-UI
 * @requires React
 */
import { AlertColor, Snackbar, SxProps, Theme } from "@mui/material";
import { Alert } from "@mui/material";

/**
 * Props interface for the SnackBarMessage component.
 * @interface SuccessMessageProps
 * @property {Function} handleClose - Callback function to execute when the snackbar is closed
 * @property {string} successMessage - The message text to display in the snackbar. Empty string will hide the snackbar.
 * @property {AlertColor} [mode='success'] - The severity level of the alert, which affects its color and icon
 * @property {'left' | 'center' | 'right'} [horizontalAnchor='center'] - Horizontal positioning of the snackbar
 * @property {'top' | 'center' | 'bottom'} [verticalAnchor='bottom'] - Vertical positioning of the snackbar
 */
interface SuccessMessageProps {
  handleClose: () => void;
  message: string;
  mode?: AlertColor;
  horizontalAnchor?: "left" | "center" | "right";
  verticalAnchor?: "top" | "center" | "bottom";
  sxOverrides?: SxProps<Theme>;
}

/**
 * @name SnackBarMessage
 *
 * @description A customizable snackbar component that displays temporary notification messages.
 *
 * @param {SuccessMessageProps} props - The component props
 *
 * @returns {JSX.Element} The rendered SnackBarMessage component
 *
 * @example
 * <SnackBarMessage
 *   handleClose={() => setMessage('')}
 *   successMessage="Unable to connect to database"
 *   mode="error"
 *   horizontalAnchor="right"
 *   verticalAnchor="top"
 * />
 *
 */
export const SnackBarMessage = ({
  handleClose,
  message,
  mode = "success",
  horizontalAnchor = "center",
  verticalAnchor = "bottom",
  sxOverrides,
}: SuccessMessageProps) => {
  // MUI Snackbar only supports 'top' and 'bottom' for vertical anchor
  // For 'center', we use 'top' and apply custom styling
  const isVerticalCenter = verticalAnchor === "center";
  const muiVerticalAnchor = isVerticalCenter ? "top" : verticalAnchor;

  return (
    <Snackbar
      // Snackbar is only visible when there is a message to display
      open={message !== ""}
      // Auto-hide after 6 seconds (6000ms)
      autoHideDuration={6000}
      // Handler for closing the snackbar when the duration expires
      onClose={handleClose}
      // Position the snackbar according to provided anchor properties
      anchorOrigin={{
        horizontal: horizontalAnchor,
        vertical: muiVerticalAnchor,
      }}
      // Apply custom styling to center vertically when needed
      sx={[
        isVerticalCenter
          ? {
              top: "50% !important",
              transform: "translateY(-50%)",
            }
          : {},
        ...(Array.isArray(sxOverrides) ? sxOverrides : sxOverrides ? [sxOverrides] : []),
      ]}
    >
      <Alert
        // Handler for closing the alert when the close button is clicked
        onClose={handleClose}
        // Sets the visual style of the alert based on the severity level
        severity={mode}
        sx={{
          width: "100%",
          fontSize: "16px",
          fontWeight: 500,
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          backdropFilter: "blur(8px)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          "& .MuiAlert-message": {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            width: "100%",
          },
          "& .MuiAlert-icon": {
            fontSize: "24px",
          },
        }}
      >
        {/* The message to display to the user */}
        {message}
      </Alert>
    </Snackbar>
  );
};
